import React from 'react';

import { useAppFieldContext } from '@/app/lib/tanstack-form';
import { FieldInfo } from '@/common/components/field-info';
import { Description, Field, Label } from '@/common/components/fieldset';
import { Listbox } from '@/common/components/listbox';

export type FormListboxProps = {
  /** The label content for the listbox. Can be a string or JSX element. */
  label: React.ReactNode;
  /** Optional description text shown below the label to provide additional context. */
  description?: React.ReactNode;
  /** ListboxOption components as children */
  children: React.ReactNode;
  /** Placeholder text when no item is selected */
  placeholder?: string;
  /** Additional CSS classes to apply to the listbox */
  className?: string;
  /** Whether to show a clear button when a value is selected */
  clearable?: boolean;
};

/**
 * A form listbox component that integrates with TanStack Form using useAppFieldContext.
 *
 * This component provides a complete form field with label, optional description,
 * listbox control, and automatic error display. It uses Catalyst UI components for
 * consistent styling and accessibility.
 *
 * @example
 * Basic usage with hardcoded options:
 * ```tsx
 * <form.AppField name="selection">
 *   <field.FormListbox label="Choose Option">
 *     <ListboxOption value="1">Option 1</ListboxOption>
 *     <ListboxOption value="2">Option 2</ListboxOption>
 *   </field.FormListbox>
 * </form.AppField>
 * ```
 *
 * @example
 * Dynamic options from a list:
 * ```tsx
 * <form.AppField name="client">
 *   <field.FormListbox label="Select Client">
 *     {clients.map(client => (
 *       <ListboxOption key={client.id} value={client.id.toString()}>
 *         <ListboxLabel>{client.name}</ListboxLabel>
 *         {client.active && <ListboxDescription>Active</ListboxDescription>}
 *       </ListboxOption>
 *     ))}
 *   </field.FormListbox>
 * </form.AppField>
 * ```
 */
export function FormListbox({
  label,
  description,
  children,
  placeholder,
  className,
  clearable = false
}: FormListboxProps) {
  const field = useAppFieldContext();

  return (
    <Field>
      <Label htmlFor={field.name}>{label}</Label>
      {description && <Description>{description}</Description>}
      <Listbox
        name={field.name}
        value={field.state.value}
        onChange={field.handleChange}
        invalid={field.state.meta.isTouched && !field.state.meta.isValid}
        placeholder={placeholder}
        className={className}
        clearable={clearable}
      >
        {children}
      </Listbox>
      <FieldInfo field={field} />
    </Field>
  );
}
